// Imports
import React from 'react';
import { Link } from 'react-router-dom';
import determineMonthlySMSCreditAllotment from '../../../../shareables/utils/billing/determine-monthly-sms-credit-allotment';
import determineEmailAccountLimit from '../../../../shareables/utils/billing/determine-email-account-limit';
import planMapping from '../../../../shareables/utils/billing/plan-mapping';
import availableSubscriptionSizes from '../../../../shareables/utils/billing/available-subscription-sizes';
import InfoIconTooltip from '../shared-components/info-icon';


// Types
type BaseFeatureValue = string | number | boolean | JSX.Element;

type SizeBasedFeatureValue = (size: number) => BaseFeatureValue;

type FeatureValue = BaseFeatureValue | SizeBasedFeatureValue;

export interface Feature {
	label: {
		text: string;
		note?: string;
		tooltip?: string;
	};
	free: FeatureValue;
	inform: FeatureValue;
	engage: FeatureValue;
}

export interface FeatureGroup {
	label: string;
	features: Feature[];
}


// Feature groupings
const featureGroupings: FeatureGroup[] = [
	{
		label: 'Core',
		features: [
			{
				label: { text: 'Pages & storage' },
				free: 'Unlimited',
				inform: 'Unlimited',
				engage: 'Unlimited',
			},
			{
				label: { text: 'Residents' },
				free: 'Unlimited',
				inform: 'Unlimited',
				engage: 'Unlimited',
			},
			{
				label: { text: 'Households' },
				free: 50,
				inform: (size) => {
					// Initialize
					const maxInformSize = availableSubscriptionSizes(planMapping.inform).pop();
					
					
					// Return JSX
					return (
						<React.Fragment>
							{size <= (maxInformSize ?? 0) ? size.toLocaleString() : maxInformSize}
							<InfoIconTooltip tooltipText='Adjust this using the slider above' />
						</React.Fragment>
					);
				},
				engage: (size) => (
					<React.Fragment>
						{size.toLocaleString()}
						<InfoIconTooltip tooltipText='Adjust this using the slider above' />
					</React.Fragment>
				),
			},
			{
				label: {
					text: 'What’s Happening emails',
					tooltip:
						'Curated emails showcasing upcoming events within your community and new activity on the website. Great for keeping residents engaged.',
				},
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: {
					text: 'Automatic address accuracy',
					tooltip:
						'Built-in features for verifying addresses, intelligently correcting minor typos, and standardizing small formatting differences (like “St” vs. “Street”).',
				},
				free: false,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Built-in visitor tracking' },
				free: false,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'No ads' },
				free: false,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Multiple website admins' },
				free: false,
				inform: false,
				engage: true,
			},
			{
				label: {
					text: 'Organize residents into groups',
					tooltip: 'Especially useful when sending communications and restricting pages.',
				},
				free: false,
				inform: false,
				engage: true,
			},
		],
	},
	{
		label: 'Communication',
		features: [
			{
				label: {
					text: 'Blast emails',
					tooltip:
						'Send announcements to the whole community—or just a subset—with rich text editing and the ability to attach files.',
				},
				free: false,
				inform: false,
				engage: true,
			},
			{
				label: {
					text: 'Blast text messages',
					tooltip: 'Great for quickly sending short announcements with high visibility.',
				},
				free: false,
				inform: false,
				engage: (size) => (
					<React.Fragment>
						{determineMonthlySMSCreditAllotment({ plan: planMapping.engage, size }).toLocaleString()} monthly{' '}
						<Link to='/pricing-details/text-messages'>credits</Link>
						<InfoIconTooltip tooltipText='Increases automatically with the number of households' />
					</React.Fragment>
				),
			},
			{
				label: { text: 'Mailed letters', note: 'U.S. recipients only' },
				free: false,
				inform: false,
				engage: <Link to='/pricing-details/mailed-letters'>Affordable pricing</Link>,
			},
			{
				label: { text: 'Surveys & polls' },
				free: false,
				inform: false,
				engage: true,
			},
			{
				label: { text: 'Resident-initiated alerts', tooltip: 'Especially useful for safety-related messages.' },
				free: false,
				inform: false,
				engage: true,
			},
		],
	},
	{
		label: 'Domain & email',
		features: [
			{
				label: {
					text: 'Free custom domain',
					tooltip: 'Your community’s memorable home on the internet, like examplehoa.com.',
				},
				free: false,
				inform: true,
				engage: true,
			},
			{
				label: {
					text: 'Professional email accounts',
					tooltip:
						'Keep community emails separate from personal emails by creating email accounts on your community’s domain, like board@examplehoa.com.',
				},
				free: false,
				inform: false,
				engage: (size) => (
					<React.Fragment>
						{determineEmailAccountLimit({ plan: planMapping.engage, size })}
						<InfoIconTooltip tooltipText='Increases automatically with the number of households' />
					</React.Fragment>
				),
			},
			{
				label: { text: 'Storage per email account' },
				free: false,
				inform: false,
				engage: '8 GB',
			},
			{
				label: { text: 'Sync via IMAP/POP' },
				free: false,
				inform: false,
				engage: true,
			},
			{
				label: {
					text: 'Email forwarding',
					tooltip: 'Automatically forward incoming emails to one or more email addresses.',
				},
				free: false,
				inform: false,
				engage: true,
			},
			{
				label: {
					text: 'Email aliases',
					tooltip:
						'Send and receive emails from alternate email addresses. For example, if John Doe is the treasurer, you might have a “treasurer@” email account with a “john@” email alias (or vice versa).',
				},
				free: false,
				inform: false,
				engage: true,
			},
			{
				label: { text: 'Fully featured webmail' },
				free: false,
				inform: false,
				engage: true,
			},
		],
	},
	{
		label: 'Website content',
		features: [
			{
				label: { text: 'General text' },
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Documents' },
				free: 'Limited',
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Calendars' },
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Board members' },
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Committees' },
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'News articles' },
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Resident directory' },
				free: false,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Pet directory' },
				free: false,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'FAQs' },
				free: false,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Maps' },
				free: false,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Community facilities' },
				free: false,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Photo galleries' },
				free: false,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Useful contact info' },
				free: false,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Custom forms' },
				free: false,
				inform: false,
				engage: true,
			},
			{
				label: { text: 'Forums' },
				free: false,
				inform: false,
				engage: true,
			},
			{
				label: { text: 'Classifieds' },
				free: false,
				inform: false,
				engage: true,
			},
			{
				label: { text: 'Garage sales' },
				free: false,
				inform: false,
				engage: true,
			},
			{
				label: { text: 'Recipes' },
				free: false,
				inform: false,
				engage: true,
			},
		],
	},
	{
		label: 'Payments',
		features: [
			{
				label: { text: 'Collect payments from residents', note: 'Select countries' },
				free: false,
				inform: false,
				engage: true,
			},
			{
				label: { text: 'Pass transaction fees to residents' },
				free: false,
				inform: false,
				engage: true,
			},
			{
				label: { text: 'Accept all major credit/debit cards' },
				free: false,
				inform: false,
				engage: true,
			},
			{
				label: { text: 'Accept bank accounts', note: 'U.S. only' },
				free: false,
				inform: false,
				engage: true,
			},
		],
	},
	{
		label: 'Security',
		features: [
			{
				label: {
					text: 'Restrict access to pages',
					tooltip: 'Fine-grained control over who can view and interact with each page on the website.',
				},
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Require approval for new registrations' },
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: {
					text: 'Complete HTTPS',
					tooltip: 'We encrypt all traffic to and from our platform—a crucial measure for preventing common attacks.',
				},
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: {
					text: 'Strict HSTS',
					tooltip: 'A modern security technique that prevents traffic from bypassing encryption.',
				},
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: {
					text: 'Full privacy controls',
					tooltip:
						'Residents have detailed control over how—and whether—their information is displayed on the website.',
				},
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: {
					text: 'Email scraping prevention',
					tooltip: 'Robust security measures that mitigate bots collecting email addresses from your website.',
				},
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Spam protection' },
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: { text: 'Frequent backups' },
				free: true,
				inform: true,
				engage: true,
			},
		],
	},
	{
		label: 'Customer success',
		features: [
			{
				label: {
					text: 'Phone support',
				},
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: {
					text: 'Chat support',
				},
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: {
					text: 'Email support',
				},
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: {
					text: 'Fast response times',
					tooltip: 'Our team prioritizes helpfulness & accuracy, but we typically reply within a few business hours.',
				},
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: {
					text: 'Personalized onboarding',
					tooltip:
						'We want your community to be successful using our platform! Schedule a 1-on-1 video call where we provide guidance on initial setup, share best practices, explain fundamental concepts, and answer any questions. We also proactively check in afterward to ensure success.',
				},
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: {
					text: 'Extensive help center',
					tooltip:
						'Nearly 200 articles (and counting) that answer common questions with step-by-step instructions, screenshots, and video walkthroughs.',
				},
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: {
					text: 'Blog with tips & tricks',
				},
				free: true,
				inform: true,
				engage: true,
			},
			{
				label: {
					text: 'Website reviews',
					tooltip:
						'Personalized recommendations for your website based on our experience helping thousands of communities.',
				},
				free: false,
				inform: true,
				engage: true,
			},
		],
	},
];

export default featureGroupings;
